import type { AccountRole } from "./types";

type RoleOperationError = {
  message?: string | null;
};

type CreateUserResponse = {
  data?: unknown;
  error?: RoleOperationError | null;
};

type CreateUserWithRolesDependencies = {
  // Creates the auth user. The et-auth create hook seeds account_role to
  // STUDENT and account_type to user; the desired role is applied afterwards
  // via updateAccount. Creating a user never writes account_type.
  createUser: (input: {
    email: string;
    password: string;
    name: string;
    username?: string;
  }) => Promise<CreateUserResponse>;
  // Writes account_role through the ai-tutor account PATCH proxy.
  updateAccount: (input: {
    userId: string;
    account_role: AccountRole;
  }) => Promise<unknown>;
};

type CreateUserWithRolesInput = {
  email: string;
  password: string;
  name: string;
  username?: string;
  account_role: AccountRole;
};

export type CreateUserWithRolesResult =
  | {
      success: true;
      account_role: AccountRole;
      userId: string;
    }
  | {
      success: false;
      message: string;
      stage: "create" | "account";
      userId?: string;
    };

export const extractCreatedUserId = (data: unknown): string | null => {
  if (!data || typeof data !== "object") {
    return null;
  }

  const record = data as Record<string, unknown>;
  const directId = record.id;
  if (typeof directId === "string" || typeof directId === "number") {
    return String(directId);
  }

  const user = record.user;
  if (!user || typeof user !== "object") {
    return null;
  }

  const userId = (user as Record<string, unknown>).id;
  if (typeof userId === "string" || typeof userId === "number") {
    return String(userId);
  }

  return null;
};

export const createUserAndAssignRoles = async (
  dependencies: CreateUserWithRolesDependencies,
  input: CreateUserWithRolesInput,
): Promise<CreateUserWithRolesResult> => {
  const createResponse = await dependencies.createUser({
    email: input.email.trim(),
    password: input.password,
    name: input.name,
    username: input.username?.trim() || undefined,
  });

  if (createResponse.error) {
    return {
      success: false,
      stage: "create",
      message: createResponse.error.message || "Failed to create user",
    };
  }

  const userId = extractCreatedUserId(createResponse.data);
  if (!userId) {
    return {
      success: false,
      stage: "create",
      message: "Created user response did not include a user id.",
    };
  }

  try {
    await dependencies.updateAccount({
      userId,
      account_role: input.account_role,
    });
  } catch (error) {
    return {
      success: false,
      stage: "account",
      userId,
      message:
        error instanceof Error ? error.message : "Failed to set account fields",
    };
  }

  return {
    success: true,
    account_role: input.account_role,
    userId,
  };
};
